import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { LogOut, Settings, HelpCircle, TrendingUp, ArrowRight, Lock, GraduationCap } from 'lucide-react';
import { TOTAL_LESSONS } from '@/components/SpeakingPractice';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXCAVATION_TOTAL_PROMPTS = 90;
const EXCAVATION_TOTAL_PHASES = 10;
const FLOWGRID_TOTAL_SESSIONS = 10;

/**
 * MainHub — Dark theme matching the Listening Chain HTML aesthetic.
 *
 * Palette:
 *  - bg:        #1A1814  (warm obsidian)
 *  - surface:   #252018  (card)
 *  - ivory:     #F5F2EC  (primary text)
 *  - accent:    #C4440A  (burnt orange)
 *  - sage:      #5DCAA5  (positive accent)
 */

const PhysicalArchitectureIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="12" r="6" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M32 18V32M32 32L22 48M32 32L42 48M32 22L18 30M32 22L46 30" stroke="currentColor" strokeWidth="1.8" />
    <line x1="8" y1="20" x2="56" y2="20" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
    <line x1="8" y1="32" x2="56" y2="32" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
    <line x1="8" y1="44" x2="56" y2="44" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
    <line x1="20" y1="6" x2="20" y2="58" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
    <line x1="32" y1="6" x2="32" y2="58" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
    <line x1="44" y1="6" x2="44" y2="58" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
  </svg>
);

const VoiceIntegrationIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="26" y="8" width="12" height="24" rx="6" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M20 24V28C20 34.627 25.373 40 32 40C38.627 40 44 34.627 44 28V24" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <line x1="32" y1="40" x2="32" y2="48" stroke="currentColor" strokeWidth="1.8" />
    <line x1="24" y1="48" x2="40" y2="48" stroke="currentColor" strokeWidth="1.8" />
    <path d="M48 20C50 22 51 25 51 28C51 31 50 34 48 36" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    <path d="M52 16C55 19 57 23 57 28C57 33 55 37 52 40" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    <path d="M16 20C14 22 13 25 13 28C13 31 14 34 16 36" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    <path d="M12 16C9 19 7 23 7 28C7 33 9 37 12 40" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
  </svg>
);

const ExcavationWritingIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="12" y="8" width="36" height="44" rx="2" stroke="currentColor" strokeWidth="1.8" fill="none" strokeOpacity="0.35" />
    <rect x="16" y="12" width="36" height="44" rx="2" stroke="currentColor" strokeWidth="1.8" fill="none" strokeOpacity="0.6" />
    <rect x="20" y="16" width="36" height="44" rx="2" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M44 28L52 20L56 24L48 32L44 32V28Z" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <line x1="44" y1="32" x2="42" y2="34" stroke="currentColor" strokeWidth="1.8" />
    <line x1="26" y1="26" x2="38" y2="26" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.65" />
    <line x1="26" y1="32" x2="40" y2="32" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.65" />
    <line x1="26" y1="38" x2="36" y2="38" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.65" />
    <line x1="26" y1="44" x2="42" y2="44" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.65" />
    <line x1="26" y1="50" x2="34" y2="50" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.65" />
  </svg>
);

const OperationListenIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="4" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M22 22 Q17 27 17 32 T22 42" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M42 22 Q47 27 47 32 T42 42" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M14 16 Q6 24 6 32 T14 48" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeOpacity="0.55" />
    <path d="M50 16 Q58 24 58 32 T50 48" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeOpacity="0.55" />
  </svg>
);

const FlowGridReadyPill = ({ prefs }) => {
  const sessions = prefs?.total_sessions_completed || 0;
  if (sessions === 0) {
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A1814] border border-[#F5F2EC]/10"
        data-testid="flowgrid-ready-pill"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#F5F2EC]/50">Ready</span>
        <span className="text-xs text-[#F5F2EC]/75 font-medium">Start your first workout</span>
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#1A1814] border border-[#C4440A]/25"
      data-testid="flowgrid-ready-pill"
    >
      <div className="flex flex-col">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#F5F2EC]/50">Workouts</span>
        <span className="text-[13px] text-[#F5F2EC] font-medium leading-tight mt-0.5">
          <span className="text-[#C4440A]">{sessions}</span> completed
        </span>
      </div>
      <span className="text-[11px] text-[#5DCAA5] font-medium tabular-nums">
        {sessions * FLOWGRID_TOTAL_SESSIONS} sessions
      </span>
    </div>
  );
};

const BreathworkResumePill = ({ progress }) => {
  const sessions = progress?.total_sessions_completed || 0;
  const best = progress?.best_ever || 0;
  if (sessions === 0) {
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A1814] border border-[#86AC93]/25"
        data-testid="breathwork-resume-pill"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#86AC93]/80">Breath</span>
        <span className="text-xs text-[#F5F2EC]/75 font-medium">Try Control Pause</span>
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#1A1814] border border-[#86AC93]/40"
      data-testid="breathwork-resume-pill"
    >
      <div className="flex flex-col">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#86AC93]/80">Breath</span>
        <span className="text-[13px] text-[#F5F2EC] font-medium leading-tight mt-0.5">
          <span className="text-[#86AC93]">{sessions}</span> session{sessions === 1 ? '' : 's'}
        </span>
      </div>
      <span className="text-[11px] text-[#86AC93] font-medium tabular-nums">
        best {best.toFixed(1)}s
      </span>
    </div>
  );
};

const VoiceResumePill = ({ progress }) => {
  if (!progress) return null;
  const day = Math.min(progress.current_day || 1, TOTAL_LESSONS);
  const pct = ((day - 1) / TOTAL_LESSONS) * 100;
  const locked = progress.is_locked === true;
  return (
    <div
      className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#1A1814] border border-[#C4440A]/25 max-w-full"
      data-testid="voice-resume-pill"
    >
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#F5F2EC]/50">Resume</span>
        <span className="text-[13px] text-[#F5F2EC] font-medium leading-tight mt-0.5 whitespace-nowrap">
          Day <span className="text-[#C4440A]">{day}</span> of {TOTAL_LESSONS}
        </span>
      </div>
      <div className="w-16 h-1 bg-[#F5F2EC]/10 rounded-full overflow-hidden flex-shrink-0">
        <div className="h-full bg-[#C4440A] rounded-full" style={{ width: `${pct}%` }} />
      </div>
      {locked ? (
        <span className="inline-flex items-center gap-1 text-[11px] text-[#F5F2EC]/50 font-medium flex-shrink-0">
          <Lock className="w-3 h-3" />
          Locked
        </span>
      ) : (
        <span className="text-[11px] text-[#5DCAA5] font-medium flex-shrink-0">Unlocked</span>
      )}
    </div>
  );
};

const ExcavationResumePill = ({ progress }) => {
  if (!progress) return null;
  const { completed, currentPrompt, phasesCompleted } = progress;
  if (completed === 0) {
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A1814] border border-[#F5F2EC]/10"
        data-testid="excavation-resume-pill"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#F5F2EC]/50">Ready</span>
        <span className="text-xs text-[#F5F2EC]/75 font-medium">Begin phase 1</span>
      </div>
    );
  }
  const pct = (completed / EXCAVATION_TOTAL_PROMPTS) * 100;
  const isComplete = completed >= EXCAVATION_TOTAL_PROMPTS;
  const displayPrompt = isComplete ? EXCAVATION_TOTAL_PROMPTS : Math.min(currentPrompt, EXCAVATION_TOTAL_PROMPTS);
  return (
    <div
      className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#1A1814] border border-[#C4440A]/25"
      data-testid="excavation-resume-pill"
    >
      <div className="flex flex-col">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#F5F2EC]/50">
          {isComplete ? 'Complete' : 'Resume'}
        </span>
        <span className="text-[13px] text-[#F5F2EC] font-medium leading-tight mt-0.5">
          Prompt <span className="text-[#C4440A]">{displayPrompt}</span> of {EXCAVATION_TOTAL_PROMPTS}
        </span>
      </div>
      <div className="w-16 h-1 bg-[#F5F2EC]/10 rounded-full overflow-hidden">
        <div className="h-full bg-[#C4440A] rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-[#5DCAA5] font-medium tabular-nums">
        {phasesCompleted}/{EXCAVATION_TOTAL_PHASES} phases
      </span>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, subtitle, description, onClick, available = true, index, pill = null }) => (
  <button
    onClick={onClick}
    disabled={!available}
    data-testid={`hub-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    className={`group relative w-full text-left p-7 sm:p-8 rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col
      ${available
        ? 'bg-[#252018] border-[#F5F2EC]/[0.08] hover:border-[#C4440A]/60 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#C4440A]/10 cursor-pointer'
        : 'bg-[#252018]/50 border-[#F5F2EC]/[0.05] cursor-not-allowed opacity-50'
      }`}
  >
    {/* Top-right arrow only */}
    <div className="flex items-center justify-end mb-6">
      {available && (
        <ArrowRight className="w-4 h-4 text-[#F5F2EC]/60 group-hover:text-[#C4440A] group-hover:translate-x-1 transition-all duration-300" />
      )}
    </div>

    {/* Icon */}
    <div className={`w-14 h-14 mb-6 rounded-xl flex items-center justify-center transition-all duration-300
      ${available
        ? 'bg-[#C4440A]/10 group-hover:bg-[#C4440A]/20'
        : 'bg-[#F5F2EC]/5'
      }`}
    >
      <Icon className={`w-9 h-9 ${available ? 'text-[#C4440A]' : 'text-[#F5F2EC]/60'}`} />
    </div>

    {/* Title */}
    <h3 className={`font-serif text-2xl sm:text-[28px] leading-tight mb-3
      ${available ? 'text-[#F5F2EC]' : 'text-[#F5F2EC]/65'}`}
    >
      {title}
    </h3>

    {/* Description */}
    <p className={`text-[15px] leading-relaxed
      ${available ? 'text-[#F5F2EC]/75' : 'text-[#F5F2EC]/60'}`}
    >
      {description}
    </p>

    {/* Progress pill */}
    {pill && (
      <div className="mt-6" onClick={(e) => e.stopPropagation()}>
        {pill}
      </div>
    )}

    {!available && (
      <div className="absolute top-4 right-4 px-2 py-1 bg-[#F5F2EC]/5 rounded text-[10px] text-[#F5F2EC]/50 font-medium tracking-wider uppercase">
        Coming Soon
      </div>
    )}
  </button>
);

const MainHub = () => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const [voiceProgress, setVoiceProgress] = useState(null);
  const [flowGridPrefs, setFlowGridPrefs] = useState(null);
  const [breathworkProgress, setBreathworkProgress] = useState(null);
  const [excavationProgress, setExcavationProgress] = useState(null);

  useEffect(() => {
    // Excavation progress lives in localStorage (no backend endpoint) — normalize into pill shape
    try {
      const raw = localStorage.getItem('excavation90Entries');
      const parsed = raw ? JSON.parse(raw) : {};
      const completed = Object.keys(parsed).length;
      const phasesCompleted = Math.floor(completed / 9);
      setExcavationProgress({
        completed,
        currentPrompt: Math.min(completed + 1, EXCAVATION_TOTAL_PROMPTS),
        phasesCompleted,
      });
    } catch (_e) {
      setExcavationProgress({ completed: 0, currentPrompt: 1, phasesCompleted: 0 });
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('gntl_token');
    if (!token || !API_URL) return;

    // Voice Integration — current day + lockout state
    fetch(`${API_URL}/api/speaking-practice/progress`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setVoiceProgress(data))
      .catch(() => {});

    // FlowGrid — total_sessions_completed (best effort; endpoint may not exist)
    fetch(`${API_URL}/api/flow-grid/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setFlowGridPrefs(data))
      .catch(() => {});

    // Breathwork — total_sessions_completed + best_ever
    fetch(`${API_URL}/api/breathwork/progress`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setBreathworkProgress(data))
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const features = [
    {
      icon: PhysicalArchitectureIcon,
      title: 'The Body',
      subtitle: 'The Grid',
      description: 'Your body is the house your voice lives in. Train coordinates, not muscles. Chambers 1–5, Levels 1–5.',
      onClick: () => navigate('/client/flow-grid'),
      available: true,
      pill: (
        <div className="flex flex-wrap items-center gap-2">
          <FlowGridReadyPill prefs={flowGridPrefs} />
          <BreathworkResumePill progress={breathworkProgress} />
        </div>
      ),
    },
    {
      icon: VoiceIntegrationIcon,
      title: 'The Voice',
      subtitle: 'Four modules',
      description: 'Tonal Influence audiobook, The Listening Chain framework, AAA Checklist, and voice drills.',
      onClick: () => navigate('/client/voice'),
      available: true,
      pill: voiceProgress ? <VoiceResumePill progress={voiceProgress} /> : null,
    },
    {
      icon: ExcavationWritingIcon,
      title: 'The Story',
      subtitle: 'Story Archaeology',
      description: 'Journal prompts to excavate how tone shaped your life and architect a new narrative from your history.',
      onClick: () => navigate('/client/excavation'),
      available: true,
      pill: excavationProgress ? <ExcavationResumePill progress={excavationProgress} /> : null,
    },
    {
      icon: OperationListenIcon,
      title: 'Operation Listen',
      subtitle: 'Live drill',
      description: 'Real-time multiplayer listening drill. Join a room by 4-digit code or create your own — Coach, Guide, Conflict, Conflict Coach.',
      onClick: () => navigate('/client/operation-listen'),
      available: true,
      pill: (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C45A3B]/15 border border-[#C45A3B]/30 text-[#C45A3B] text-[10px] uppercase tracking-[0.18em]" data-testid="olg-pill">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C45A3B] animate-pulse" />
            Live · 2–4 players
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate('/client/operation-listen?mode=facilitator');
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#F5F2EC]/15 text-[10px] uppercase tracking-[0.18em] text-[#F5F2EC]/70 hover:text-[#C45A3B] hover:border-[#C45A3B]/50 transition-colors"
            data-testid="olg-facilitator-quicklaunch"
          >
            I'm the facilitator <span aria-hidden="true">→</span>
          </button>
        </div>
      ),
    },
    {
      icon: ({ className }) => <GraduationCap className={className} strokeWidth={1.4} />,
      title: "Student's Lounge",
      subtitle: 'Companion mode',
      description: "Follow along with a teacher's live presentation. Enter the 4-digit code from Teacher's Corner and your phone mirrors the slide.",
      onClick: () => navigate('/client/students-lounge'),
      available: true,
      pill: (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C45A3B]/15 border border-[#C45A3B]/30 text-[#C45A3B] text-[10px] uppercase tracking-[0.18em]"
          data-testid="students-lounge-pill"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#C45A3B] animate-pulse" />
          Join by code
        </span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#1A1814] text-[#F5F2EC]" data-testid="main-hub">
      {/* Header */}
      <header className="border-b border-[#F5F2EC]/[0.08] bg-[#1A1814]/90 backdrop-blur-sm sticky top-0 z-10 pt-safe">
        <div className="container mx-auto px-6 py-5 flex justify-between items-center">
          <div>
            <h1 className="font-serif text-2xl tracking-tight text-[#F5F2EC]">
              <span className="lowercase">gntl</span>
              <em className="italic mx-1 text-[#F5F2EC]/60">·</em>
              <span className="uppercase tracking-[0.15em]">strngth</span>
            </h1>
            <p className="text-xs text-[#F5F2EC]/50 mt-0.5">
              Welcome, <span className="text-[#F5F2EC]/85 font-medium">{user?.name || 'Architect'}</span>
            </p>
          </div>
          <nav className="flex items-center gap-1">
            <Link
              to="/client/progress"
              data-testid="nav-progress"
              className="p-2.5 rounded-lg text-[#F5F2EC]/50 hover:text-[#C4440A] hover:bg-[#F5F2EC]/5 transition-colors"
              title="Your Progress"
            >
              <TrendingUp className="w-[18px] h-[18px]" />
            </Link>
            <Link
              to="/client/help"
              data-testid="nav-help"
              className="p-2.5 rounded-lg text-[#F5F2EC]/50 hover:text-[#C4440A] hover:bg-[#F5F2EC]/5 transition-colors"
              title="Help & Support"
            >
              <HelpCircle className="w-[18px] h-[18px]" />
            </Link>
            <Link
              to="/client/settings"
              data-testid="nav-settings"
              className="p-2.5 rounded-lg text-[#F5F2EC]/50 hover:text-[#C4440A] hover:bg-[#F5F2EC]/5 transition-colors"
              title="Settings"
            >
              <Settings className="w-[18px] h-[18px]" />
            </Link>
            <button
              onClick={handleLogout}
              data-testid="nav-logout"
              className="ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F5F2EC]/15 text-[#F5F2EC]/70 text-sm hover:border-[#F5F2EC]/35 hover:text-[#F5F2EC] transition-all"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="container mx-auto px-6 py-16 sm:py-20">
        {/* Hero */}
        <div className="max-w-3xl mb-16 sm:mb-20">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-8 border border-[#F5F2EC]/15 bg-[#F5F2EC]/[0.03]">
            <div className="w-2 h-2 rounded-full bg-[#C4440A]" />
            <span className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#F5F2EC]/70">
              Presence Calibration
            </span>
          </div>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#F5F2EC] mb-6">
            The architecture
            <br />
            of <em className="italic text-[#C4440A]">you.</em>
          </h2>
          <p className="text-lg sm:text-xl text-[#F5F2EC]/75 leading-relaxed max-w-2xl">
            Strength in a world that stands still. Strength that ensures you still stand.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 max-w-6xl">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              subtitle={feature.subtitle}
              description={feature.description}
              onClick={feature.onClick}
              available={feature.available}
              index={index}
              pill={feature.pill}
            />
          ))}
        </div>

        {/* Pull-quote */}
        <div className="mt-24 sm:mt-28 max-w-2xl">
          <div className="text-[10px] font-medium tracking-[0.25em] uppercase text-[#F5F2EC]/65 mb-4">
            The Principle
          </div>
          <p className="font-serif text-3xl sm:text-4xl text-[#F5F2EC] leading-tight">
            Be strong enough to be <em className="italic text-[#C4440A]">gentle.</em>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#F5F2EC]/[0.08] py-8 mt-20">
        <div className="container mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#F5F2EC]/65 tracking-wider">
            GNTL STRNGTH v1.0 — Presence Calibration Platform
          </p>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#5DCAA5]" />
            <span className="text-[11px] text-[#F5F2EC]/50">Session active</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainHub;
